<script lang="ts">
  // The company create/edit form — extracted from CompaniesPage so
  // SitesPage's "New site" (kind=hotel) and OwnersPage's "New owner"/edit
  // (kind=owner) can reuse the exact same fields/validation instead of a
  // second copy (see the Hotels nav restructure spec, requirement 3).
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { session } from '$lib/state/session.svelte';
  import { companiesApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomerIntakeFields from '$lib/components/ui/CustomerIntakeFields.svelte';
  import CompanyContacts from './CompanyContacts.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';
  import type { Company } from '$lib/types';

  const {
    editing = null,
    defaultKind = 'company',
    lockKind = false,
    defaultVerticalId,
    lockVertical = false,
    onClose,
    onSaved
  }: {
    // Non-null puts the form in edit mode, pre-filled from this row.
    editing?: Company | null;
    // Create-mode initial kind (ignored when editing).
    defaultKind?: string;
    // Hides the Kind selector — Sites/Owners pages fix it to hotel/owner so
    // "New site"/"New owner" can't accidentally create the wrong kind.
    lockKind?: boolean;
    // Create-mode initial vertical; falls back to the caller's first
    // accessible vertical when omitted (CompaniesPage's "all verticals" case).
    defaultVerticalId?: number;
    // Hides the Vertical selector — Sites/Owners pages are already scoped to
    // one vertical, so there's nothing to pick.
    lockVertical?: boolean;
    onClose: () => void;
    onSaved?: (company: Company) => void;
  } = $props();

  const queryClient = useQueryClient();

  const KIND_LABEL: Record<string, string> = {
    hotel: 'Property',
    brand: 'Brand',
    owner: 'Owner',
    company: 'Company'
  };
  function kindLabel(k: string): string {
    return KIND_LABEL[k] ?? k;
  }

  const partnerStatuses: Array<{ value: string; label: string }> = [
    { value: 'prospect', label: 'Prospect' },
    { value: 'in_discussion', label: 'In discussion' },
    { value: 'active_partner', label: 'Active partner' },
    { value: 'dormant', label: 'Dormant' }
  ];

  function emptyForm() {
    const verticalId = defaultVerticalId ?? session.me?.verticals[0]?.id;
    return {
      vertical_id: verticalId,
      kind: intakeKind(verticalId, defaultKind),
      name: '',
      city: '',
      phone: '',
      email: '',
      website: '',
      owner_company_id: undefined as number | undefined,
      brand_company_id: undefined as number | undefined,
      partner_status: '',
      custom: {} as Record<string, unknown>
    };
  }

  function intakeKind(verticalId: number | undefined, kind: string): string {
    if (lockKind) return kind;
    const hotels = session.me?.verticals.find(v => v.id === verticalId)?.slug === 'hotels';
    return hotels ? (kind === 'brand' || kind === 'owner' ? kind : 'hotel') : 'company';
  }

  // form is keyed off `editing` via this effect (not a $state initializer)
  // so that if a modal instance is ever reused across records — or just
  // toggled between create/edit — the fields reset to the new record
  // instead of freezing on whichever `editing` value was live at mount.
  let form = $state(emptyForm());
  $effect(() => {
    form = editing
      ? {
          vertical_id: editing.vertical_id,
          kind: editing.kind,
          name: editing.name,
          city: editing.city,
          phone: editing.phone,
          email: editing.email,
          website: editing.website,
          owner_company_id: editing.owner_company_id ?? undefined,
          brand_company_id: editing.brand_company_id ?? undefined,
          partner_status: editing.partner_status ?? '',
          custom: { ...editing.custom }
        }
      : emptyForm();
  });
  let formError = $state('');
  let contact = $state<Record<string, unknown>>({});
  let previousVertical: number | undefined;
  $effect(() => {
    const vertical = form.vertical_id;
    if (!editing && previousVertical !== vertical) {
      form.kind = intakeKind(vertical, form.kind);
    }
    if (!editing && previousVertical !== undefined && previousVertical !== vertical) {
      form.owner_company_id = undefined;
      form.brand_company_id = undefined;
      form.custom = {};
      contact = {};
    }
    previousVertical = vertical;
  });

  const customFieldDefs = $derived(session.customFieldsFor('companies', form.vertical_id));

  // Owner/brand hierarchy is a hotels-vertical, kind=hotel concept — hide
  // the pickers otherwise (fields stay optional/untouched on the backend,
  // this is purely a form-rendering decision).
  const formIsHotels = $derived(session.me?.verticals.find((v) => v.id === form.vertical_id)?.slug === 'hotels');
  const formIsProperty = $derived(formIsHotels && form.kind === 'hotel');

  const ownersQuery = createQuery(() => ({
    queryKey: ['companies-owners', { verticalId: form.vertical_id }],
    queryFn: () => companiesApi.list({ per_page: 100, kind: 'owner', vertical_id: form.vertical_id }),
    enabled: formIsProperty
  }));
  const brandsQuery = createQuery(() => ({
    queryKey: ['companies-brands', { verticalId: form.vertical_id }],
    queryFn: () => companiesApi.list({ per_page: 100, kind: 'brand', vertical_id: form.vertical_id }),
    enabled: formIsProperty
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editing ? companiesApi.update(editing.id, body) : companiesApi.create(body),
    onSuccess: (company: Company) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['company-detail', company.id] });
      if (!editing) queryClient.invalidateQueries({ queryKey: ['people'] });
      onSaved?.(company);
      onClose();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      formError = 'Name is required';
      return;
    }
    formError = '';
    saveMutation.mutate({ ...form, ...(editing ? {} : contact) });
  }

  const title = $derived(`${editing ? 'Edit' : 'New'} ${kindLabel(form.kind).toLowerCase()}`);
</script>

<Modal title={title} onClose={onClose}>
  <form id="company-form" class="space-y-4" onsubmit={submit}>
    {#if !editing && !lockVertical}
      <CustomerIntakeFields bind:verticalId={form.vertical_id} idPrefix="company-intake" />
    {:else if !editing && lockVertical}
      <p class="text-xs text-dark-gray">{formIsHotels ? 'Hotel coordination: Anitha.' : 'Non-hotel coordination: Farhath.'} Sales ownership and team access remain unchanged.</p>
    {/if}
    {#if !lockKind}
      <div>
        <label class="label" for="kind">Company structure</label>
        <select id="kind" class="input" bind:value={form.kind}>
          {#if editing}
            <option value="company">Company</option>
            <option value="hotel">Property</option>
            <option value="brand">Brand</option>
            <option value="owner">Owner</option>
          {:else if formIsHotels}
            <option value="hotel">Property</option>
            <option value="brand">Brand</option>
            <option value="owner">Owner</option>
          {:else}
            <option value="company">Non-hotel company</option>
          {/if}
        </select>
      </div>
    {/if}
    <div>
      <label class="label" for="name">Name</label>
      <input id="name" class="input" bind:value={form.name} required />
    </div>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label class="label" for="city">City</label>
        <input id="city" class="input" bind:value={form.city} />
      </div>
      <div>
        <label class="label" for="phone">Company general phone (optional)</label>
        <input id="phone" class="input" bind:value={form.phone} />
      </div>
    </div>
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label class="label" for="email">Company general email (optional)</label>
        <input id="email" type="email" class="input" bind:value={form.email} />
      </div>
      <div>
        <label class="label" for="website">Website</label>
        <input id="website" class="input" bind:value={form.website} />
      </div>
    </div>
    <div>
      <label class="label" for="partner-status">Partner status</label>
      <select id="partner-status" class="input" bind:value={form.partner_status}>
        <option value="">-- unset --</option>
        {#each partnerStatuses as ps (ps.value)}
          <option value={ps.value}>{ps.label}</option>
        {/each}
      </select>
    </div>
    {#if formIsProperty}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="owner-company">Owner</label>
          <select id="owner-company" class="input" bind:value={form.owner_company_id}>
            <option value={undefined}>-- none --</option>
            {#each ownersQuery.data?.results ?? [] as o (o.id)}
              <option value={o.id}>{o.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="brand-company">Brand</label>
          <select id="brand-company" class="input" bind:value={form.brand_company_id}>
            <option value={undefined}>-- none --</option>
            {#each brandsQuery.data?.results ?? [] as b (b.id)}
              <option value={b.id}>{b.name}</option>
            {/each}
          </select>
        </div>
      </div>
    {/if}

    {#if !editing}<CompanyContacts verticalId={form.vertical_id} bind:value={contact} />{/if}
    <CustomFieldsForm defs={customFieldDefs} values={form.custom} />

    {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}
  </form>

  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary" onclick={onClose}>Cancel</button>
      <!-- sibling of the <form>, so it submits via the `form` attribute -->
      <button type="submit" form="company-form" class="btn-primary" disabled={saveMutation.isPending}>
        {saveMutation.isPending ? 'Saving...' : 'Save'}
      </button>
    </div>
  {/snippet}
</Modal>
